import * as React from 'react'

import { SitePermitStatus } from '~/client/graph'
import {
  WorkflowStatusRole,
  workflowStatusRoles,
} from '~/client/src/shared/constants/workflowStatusColors'
import { getFormStatusDisplayName } from '~/client/src/shared/localization/enumDisplayTexts'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { cn } from '~/client/src/shared/utils/cn'

import { Badge } from '../shadcn-ui/badge'

// One class pair per status ROLE, keyed off the same map the pin resolves through — so a
// status cannot look like one thing on a badge and another on the map. The soft pair is the
// badge's own: `-soft` is the tint it sits on and `-soft-foreground` the ink that clears AA on
// it, which is why four roles' ink is not their pin's fill.
const ROLE_CLASSES: Record<
  WorkflowStatusRole,
  { fill: string; ink: string; dot: string }
> = {
  [WorkflowStatusRole.Pending]: {
    fill: 'tw:bg-status-pending-soft',
    ink: 'tw:text-status-pending-soft-foreground',
    dot: 'tw:bg-status-pending-soft-foreground',
  },
  [WorkflowStatusRole.InReview]: {
    fill: 'tw:bg-status-in-review-soft',
    ink: 'tw:text-status-in-review-soft-foreground',
    dot: 'tw:bg-status-in-review-soft-foreground',
  },
  [WorkflowStatusRole.Inspecting]: {
    fill: 'tw:bg-status-inspecting-soft',
    ink: 'tw:text-status-inspecting-soft-foreground',
    dot: 'tw:bg-status-inspecting-soft-foreground',
  },
  [WorkflowStatusRole.Approved]: {
    fill: 'tw:bg-status-approved-soft',
    ink: 'tw:text-status-approved-soft-foreground',
    dot: 'tw:bg-status-approved-soft-foreground',
  },
  [WorkflowStatusRole.Amended]: {
    fill: 'tw:bg-status-amended-soft',
    ink: 'tw:text-status-amended-soft-foreground',
    dot: 'tw:bg-status-amended-soft-foreground',
  },
  [WorkflowStatusRole.InProgress]: {
    fill: 'tw:bg-status-in-progress-soft',
    ink: 'tw:text-status-in-progress-soft-foreground',
    dot: 'tw:bg-status-in-progress-soft-foreground',
  },
  [WorkflowStatusRole.Rejected]: {
    fill: 'tw:bg-status-rejected-soft',
    ink: 'tw:text-status-rejected-soft-foreground',
    dot: 'tw:bg-status-rejected-soft-foreground',
  },
  [WorkflowStatusRole.Closed]: {
    fill: 'tw:bg-status-closed-soft',
    ink: 'tw:text-status-closed-soft-foreground',
    dot: 'tw:bg-status-closed-soft-foreground',
  },
}

// A status with no role, and a badge with no status, both read as closed — the same
// last-resort the pin takes.
const classesFor = (status?: SitePermitStatus, isLate?: boolean) =>
  ROLE_CLASSES[
    isLate
      ? WorkflowStatusRole.Rejected
      : (status && workflowStatusRoles[status]) || WorkflowStatusRole.Closed
  ]

interface IProps {
  status: SitePermitStatus | undefined
  workflowStepLevel?: number
  showDot?: boolean
  compact?: boolean
  className?: string
  isLate?: boolean
}

export default function StatusBadge({
  status,
  workflowStepLevel,
  showDot = true,
  compact = false,
  className,
  isLate = false,
}: IProps): React.ReactElement {
  const { fill, ink, dot } = classesFor(status, isLate)
  const statusName = status
    ? getFormStatusDisplayName(status, workflowStepLevel)
    : ''
  const label = isLate
    ? `${statusName}, ${Localization.translator.late}`
    : statusName

  if (compact) {
    return (
      <span
        className={cn(
          'tw:inline-flex tw:items-center tw:gap-1 tw:text-xs tw:font-medium tw:whitespace-nowrap',
          ink,
          className,
        )}
        title={label}
      >
        {showDot && (
          <span
            className={cn('tw:size-1.5 tw:shrink-0 tw:rounded-full', dot)}
            aria-hidden="true"
          />
        )}
        {label}
      </span>
    )
  }

  return (
    <Badge
      variant="outline"
      className={cn(
        'tw:mx-1 tw:overflow-visible tw:border-transparent',
        fill,
        ink,
        className,
      )}
      title={label}
    >
      {showDot && (
        <span
          className={cn('tw:size-1.5 tw:shrink-0 tw:rounded-full', dot)}
          aria-hidden="true"
        />
      )}
      {label}
    </Badge>
  )
}
